import { useCallback, useEffect, useId, useRef, useState, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { Button, IconButton } from './Button.js';
import { useFocusTrap, useInertBackground, focusableWithin } from './focus-trap.js';
import { TextField } from './Field.js';
import { useSwipeToDismiss } from './swipe-dismiss.js';
import { useIsMobile } from './use-media.js';

export type DialogCloseReason = 'escape' | 'scrim' | 'close-button' | 'swipe' | 'action';

/**
 * Where focus lands on open: the title (so the dialog is announced), the first control of the BODY, or a named element.
 */
export type DialogInitialFocus = 'title' | 'first' | RefObject<HTMLElement | null>;

/**
 * The element `initialFocus` lands on (pure, pinned under node). `'first'` takes the first focusable inside the body —
 * the `.dialog__body` that holds the form — and never the header's corner Close, which precedes the body in the panel
 * and is the first focusable of the panel as a whole: a dialog opened to type into (Share's combobox, Rename's name)
 * must land on its field, since §4.1's "the first control" means the dialog's own, not its dismissal. A body with
 * nothing focusable falls back to the title, as does a ref that is not attached.
 */
export function initialFocusTarget(
  initialFocus: DialogInitialFocus,
  parts: { title: HTMLElement | null; body: HTMLElement | null },
  focusable: (root: HTMLElement | null) => HTMLElement[] = focusableWithin,
): HTMLElement | null {
  if (initialFocus === 'first') return focusable(parts.body)[0] ?? parts.title;
  if (initialFocus === 'title') return parts.title;
  return initialFocus.current ?? parts.title;
}

/**
 * Where focus moves when the body's first control ARRIVES after open (pure, pinned under node). A body still loading its
 * form — the filing dialog's ProjectCheckboxes fetching the person's projects — holds nothing focusable at open, so
 * `'first'` fell back to the title; once the form is there, its first control is where §4.1's "the first control"
 * points, and focus moves to it. Only `'first'` asks for this, and only while focus still rests on the title where the
 * open put it: anywhere else — Cancel, the corner Close, the page — means the person has moved on, and focus is never
 * taken from them. Null means stay.
 */
export function arrivedFocusTarget(
  initialFocus: DialogInitialFocus,
  parts: { title: HTMLElement | null; body: HTMLElement | null },
  active: Element | null,
  focusable: (root: HTMLElement | null) => HTMLElement[] = focusableWithin,
): HTMLElement | null {
  if (initialFocus !== 'first') return null;
  if (parts.title === null || active !== parts.title) return null;
  return focusable(parts.body)[0] ?? null;
}

export interface DialogProps {
  open: boolean;
  /** Called when the dialog should close. The parent owns `open`. */
  onClose: (reason: DialogCloseReason) => void;
  /** The `<h2>`; the dialog is labelled by it. */
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  /** Right-aligned; cancel then confirm (03-components §4.1). */
  actions?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  role?: 'dialog' | 'alertdialog';
  /**
   * Where focus lands on open: the title (default, so the dialog is announced), the first control of the body
   * (never the corner Close — `initialFocusTarget`), or a specific element. Confirmations put it on Cancel — never
   * the destructive button.
   */
  initialFocus?: DialogInitialFocus;
  /**
   * When true, Escape and a scrim click do not discard silently: an inline prompt asks the user
   * to keep editing or discard (03-components §4.1).
   */
  unsavedChanges?: boolean;
  /** Hide the corner close button (a confirmation offers Cancel instead). */
  hideClose?: boolean;
  /**
   * Below --bp-md: a bottom sheet (the default) or, for anything of form complexity, full-screen with
   * the title and close affordance in a top bar (03-components §4.1). A dialog holding a combobox
   * takes `fullscreen`, so the list stays beneath its input rather than in a sheet under the keyboard
   * (§3.4). No effect from --bp-md up.
   */
  mobile?: 'sheet' | 'fullscreen';
  className?: string;
}

/**
 * A modal dialog on desktop and a bottom sheet (or, by `mobile`, a full-screen surface) below --bp-md
 * (03-components §4.1). Traps focus, makes the page inert, restores focus to the opener, closes on
 * Escape and scrim click, never exceeds 80vh — the body scrolls while title and actions stay put.
 * Dialogs do not nest.
 */
export function Dialog({ open, onClose, title, description, children, actions, size = 'md', role = 'dialog', initialFocus = 'title', unsavedChanges = false, hideClose = false, mobile = 'sheet', className }: DialogProps) {
  const id = useId();
  const titleId = `${id}-title`;
  const descriptionId = description ? `${id}-description` : undefined;
  const panelRef = useRef<HTMLDivElement | null>(null);
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const [discardPrompt, setDiscardPrompt] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!open) setDiscardPrompt(false);
  }, [open]);

  const pickInitialFocus = useCallback((): HTMLElement | null => initialFocusTarget(initialFocus, { title: titleRef.current, body: bodyRef.current }), [initialFocus]);

  useInertBackground(open);
  useFocusTrap(panelRef, open, pickInitialFocus);

  // Why: with `initialFocus="first"` and a body whose form is still loading, the trap lands on the title; when the
  // form arrives — the first control appears, or a control stops being disabled — focus moves to its first control,
  // once, and only while focus still rests on the title (`arrivedFocusTarget`): a person who has already moved on keeps
  // their place. Pinned by the File under… journey (test/e2e/journeys/todos.spec.ts), whose list loads after open.
  useEffect(() => {
    if (!open || initialFocus !== 'first') return;
    const body = bodyRef.current;
    if (!body || typeof MutationObserver === 'undefined') return;
    const observer = new MutationObserver(() => {
      const target = arrivedFocusTarget(initialFocus, { title: titleRef.current, body }, document.activeElement);
      if (!target) return;
      observer.disconnect();
      target.focus({ preventScroll: true });
    });
    observer.observe(body, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'hidden', 'aria-hidden'] });
    return () => observer.disconnect();
  }, [open, initialFocus]);

  const attemptClose = useCallback((reason: DialogCloseReason) => {
    if (unsavedChanges && (reason === 'escape' || reason === 'scrim' || reason === 'close-button' || reason === 'swipe')) {
      setDiscardPrompt(true);
      return;
    }
    onClose(reason);
  }, [onClose, unsavedChanges]);

  // Why the sheet answers a drag on its handle and header (03-components §4.1: sheets "SHOULD support swipe-to-dismiss —
  // but swipe MUST NOT be the only dismissal"; Phase 1c finding G10): the handle promised the gesture. A swipe is a scrim
  // tap's dismissal, so unsaved input asks first; Escape, the scrim and the close control all remain.
  useSwipeToDismiss(panelRef, open && isMobile && mobile === 'sheet', () => attemptClose('swipe'));

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && !event.isComposing) {
        event.preventDefault();
        attemptClose('escape');
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, attemptClose]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div className="dialog-layer">
      <div className="dialog-scrim" onClick={() => attemptClose('scrim')} aria-hidden="true" />
      <div
        ref={panelRef}
        className={['dialog', `dialog--${size}`, mobile === 'fullscreen' ? 'dialog--fullscreen' : '', className ?? ''].filter(Boolean).join(' ')}
        role={role}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        tabIndex={-1}
      >
        <div className="dialog__handle" aria-hidden="true" data-swipe-zone="" />
        <header className="dialog__header" data-swipe-zone="">
          <h2 id={titleId} ref={titleRef} tabIndex={-1} className="dialog__title">{title}</h2>
          {hideClose ? null : <IconButton icon="x" label="Close" onClick={() => attemptClose('close-button')} className="dialog__close" />}
        </header>
        {description ? <p id={descriptionId} className="dialog__description">{description}</p> : null}
        <div className="dialog__body" ref={bodyRef}>{children}</div>
        {discardPrompt ? (
          <div className="dialog__discard" role="alert">
            <p>You have unsaved changes.</p>
            <div className="cluster">
              <Button variant="secondary" onClick={() => setDiscardPrompt(false)} autoFocus>Keep editing</Button>
              <Button variant="danger" onClick={() => onClose('action')}>Discard changes</Button>
            </div>
          </div>
        ) : null}
        {actions ? <footer className="dialog__actions">{actions}</footer> : null}
      </div>
    </div>,
    document.body,
  );
}

export interface ConfirmDialogProps {
  open: boolean;
  /** States the consequence: "Delete 14 to-dos?" — never "Are you sure?" (03-components §4.2). */
  title: ReactNode;
  /** What is lost and whether it can be undone. */
  children?: ReactNode;
  /** The verb: "Delete", "Suspend". Never "OK". */
  confirmLabel: string;
  cancelLabel?: string;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
  /** Destructive: danger styling on the confirm button. */
  danger?: boolean;
  /** While the confirm is in flight: disables re-submission and shows a spinner. */
  busy?: boolean;
  /** For high-consequence actions: the user must type this exact text before confirm enables. */
  typeToConfirm?: string;
  /** An error from a failed attempt, shown in place. */
  error?: ReactNode;
}

/** A confirmation with the cancel button as the default focus (03-components §4.2). */
export function ConfirmDialog({ open, title, children, confirmLabel, cancelLabel = 'Cancel', onConfirm, onCancel, danger = false, busy = false, typeToConfirm, error }: ConfirmDialogProps) {
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const [typed, setTyped] = useState('');
  useEffect(() => {
    if (!open) setTyped('');
  }, [open]);
  const canConfirm = !busy && (typeToConfirm === undefined || typed.trim() === typeToConfirm);
  return (
    <Dialog
      open={open}
      onClose={() => { if (!busy) onCancel(); }}
      title={title}
      role="alertdialog"
      size="sm"
      initialFocus={cancelRef}
      hideClose
      actions={
        <>
          {/* Why the words (05-client §5.5): the confirm in flight is the loading state; Cancel beside it is inert for
              that reason and says so; a type-to-confirm that does not yet match says what to type. */}
          <Button ref={cancelRef} variant="secondary" onClick={onCancel} disabled={busy} disabledReason={busy ? 'Wait for the action to finish.' : undefined}>{cancelLabel}</Button>
          <Button
            variant={danger ? 'danger' : 'primary'}
            onClick={() => void onConfirm()}
            disabled={!canConfirm}
            disabledReason={typeToConfirm !== undefined && typed.trim() !== typeToConfirm ? `Type ${typeToConfirm} to confirm first.` : undefined}
            loading={busy}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="stack stack--3">
        {children ? <div className="dialog__text">{children}</div> : null}
        {typeToConfirm !== undefined ? (
          <TextField
            label={<>Type <strong>{typeToConfirm}</strong> to confirm</>}
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
        ) : null}
        {error ? <p className="field__error" role="alert">{error}</p> : null}
      </div>
    </Dialog>
  );
}
